import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LIBELLES_ROLE, auMoins, type ApercuEnTete, type UtilisateurPublic } from '@rh/shared';
import { appel } from '../api';
import { RelancerLecture } from './RelancerLecture';

const JOUR = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Africa/Tunis',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Africa/Tunis', hour: '2-digit', minute: '2-digit' });
const DATE_COURTE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Africa/Tunis',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/** Les chiffres bougent lentement : une relecture toutes les 5 minutes suffit. */
export const INTERVALLE_APERCU_MS = 300_000;

/** « 1 nouvelle candidature », « 4 nouvelles candidatures » : les deux mots s'accordent. */
const nouveautes = (n: number) => `${n} nouvelle${n > 1 ? 's' : ''} candidature${n > 1 ? 's' : ''}`;

/**
 * Statut affiché dans l'en-tête, sur la ligne du bouton du menu et du logo :
 * témoin de connexion, chiffres généraux (annonces en cours, candidats,
 * nouveautés depuis la dernière visite), profil connecté, rôle, date et heure
 * de Tunis. Le témoin est vert quand le téléphone joint le réseau et que la
 * session tient ; ambre sinon, pour ne jamais annoncer « connecté » à tort.
 *
 * Les chiffres viennent de `GET /stats/apercu` : relus toutes les 5 minutes et
 * au retour sur l'onglet, jamais quand il est masqué. Si l'appel échoue — hors
 * ligne, session à renouveler — le statut garde ce qu'il affichait et ne
 * montre rien tant qu'il n'a rien : un chiffre faux vaut moins que pas de
 * chiffre.
 */
export function BarreStatut({
  utilisateur,
  enLigne,
  sessionFragile,
  intervalleMs = INTERVALLE_APERCU_MS,
}: {
  utilisateur: UtilisateurPublic;
  enLigne: boolean;
  sessionFragile: boolean;
  intervalleMs?: number;
}) {
  const [maintenant, setMaintenant] = useState(() => new Date());
  const [apercu, setApercu] = useState<ApercuEnTete | null>(null);

  useEffect(() => {
    // Relue toutes les 15 s : l'heure affichée n'a jamais plus de 15 s de retard.
    const minuteur = setInterval(() => setMaintenant(new Date()), 15_000);
    return () => clearInterval(minuteur);
  }, []);

  const relire = useCallback(() => {
    if (document.visibilityState === 'hidden') return;
    appel<ApercuEnTete>('/stats/apercu')
      .then(setApercu)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    relire();
    const minuteur = setInterval(relire, intervalleMs);
    const surRetour = () => relire();
    document.addEventListener('visibilitychange', surRetour);
    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', surRetour);
    };
  }, [relire, intervalleMs]);

  const connecte = enLigne && !sessionFragile;
  const depuis = apercu?.depuis ? DATE_COURTE.format(new Date(apercu.depuis)) : null;

  return (
    <div className="barre-statut" aria-label="Barre de statut">
      <span className="etat-connexion">
        <span className={connecte ? 'temoin temoin--connecte' : 'temoin temoin--hors-ligne'} aria-hidden="true" />
        {connecte ? 'Connecté' : enLigne ? 'Connexion fragile' : 'Hors ligne'}
      </span>

      {apercu && (
        <span className="chiffres-statut">
          <Link to="/annonces" className="chiffre" title="Annonces validées ou publiées">
            <strong>{apercu.annoncesOuvertes}</strong> {apercu.annoncesOuvertes > 1 ? 'annonces' : 'annonce'}
          </Link>
          <span className="chiffre" title="Personnes qui portent au moins une candidature">
            <strong>{apercu.candidats}</strong> {apercu.candidats > 1 ? 'candidats' : 'candidat'}
          </span>
          {depuis &&
            (apercu.nouvellesCandidatures > 0 ? (
              <span className="chiffre chiffre--nouveau" title={`Depuis votre dernière connexion, le ${depuis}`}>
                {nouveautes(apercu.nouvellesCandidatures)} depuis votre dernière visite
              </span>
            ) : (
              <span className="chiffre" title={`Depuis votre dernière connexion, le ${depuis}`}>
                aucune nouvelle candidature depuis votre dernière visite
              </span>
            ))}
        </span>
      )}

      {/* Relever la boîte de candidatures à la demande, depuis n'importe quel
          écran : l'envie vient quand on attend une candidature annoncée par
          téléphone, pas quand on est sur la file d'ingestion. Réservé aux
          profils RH, comme la route (`@RoleMinimum('rh')`) — l'afficher à un
          lecteur ne lui vaudrait qu'un refus. La relecture des chiffres suit
          de quelques secondes : c'est là que « n nouvelles candidatures »
          bouge. */}
      {auMoins(utilisateur.role, 'rh') && <RelancerLecture onDemande={relire} />}

      <Link to="/mon-compte" className="profil-statut" title="Mon compte">
        <strong>{utilisateur.nom}</strong>
      </Link>
      <span className="role">{LIBELLES_ROLE[utilisateur.role]}</span>
      <time className="horloge" dateTime={maintenant.toISOString()}>
        {JOUR.format(maintenant)} · {HEURE.format(maintenant)}
      </time>
    </div>
  );
}
